import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Permission,
  roleHasPermissions,
  type DomainHealth,
  type DomainHealthStatus,
  type IntegrationHealthRow,
  type IntegrationProvider,
  type IntegrationStatus,
  type Member,
} from '@lizrd/contracts';
import { getDomainHealth, listMembers } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SkeletonRows } from '../components/ui/skeleton';
import { Pagination, usePagination } from '../components/ui/pagination';
import { Avatar } from '../components/Avatar';
import { ProviderLogo } from '../components/ProviderLogo';

// --- Relative-time (self-contained; the app has no shared helper yet). ---
function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function joinedLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// --- Presentation maps (kept alongside the view; pure and trivial). ---
type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const HEALTH_META: Record<
  DomainHealthStatus,
  { label: string; badge: BadgeVariant; stripe: string }
> = {
  healthy: { label: 'Healthy', badge: 'success', stripe: 'border-l-success' },
  attention: { label: 'Attention', badge: 'warning', stripe: 'border-l-warning' },
  error: { label: 'Error', badge: 'danger', stripe: 'border-l-danger' },
  syncing: { label: 'Syncing', badge: 'info', stripe: 'border-l-info' },
  empty: { label: 'No sources', badge: 'neutral', stripe: 'border-l-hairline' },
};

const INT_DOT: Record<IntegrationStatus, string> = {
  CONNECTED: 'bg-success',
  SYNCING: 'bg-info',
  WARNING: 'bg-warning',
  PENDING: 'bg-warning',
  ERROR: 'bg-danger',
  DISCONNECTED: 'bg-danger',
};

const INT_LABEL: Record<IntegrationStatus, string> = {
  CONNECTED: 'Connected',
  SYNCING: 'Syncing',
  WARNING: 'Warning',
  PENDING: 'Awaiting first sync',
  ERROR: 'Error',
  DISCONNECTED: 'Disconnected',
};

const PROVIDER: Record<IntegrationProvider, { label: string }> = {
  GITHUB: { label: 'GitHub' },
  GITLAB: { label: 'GitLab' },
  AWS: { label: 'AWS' },
  AZURE: { label: 'Azure' },
  GCP: { label: 'GCP' },
  SANDBOX: { label: 'Sandbox' },
};

const isBrokenIntegration = (s: IntegrationStatus) =>
  s === 'ERROR' || s === 'DISCONNECTED' || s === 'WARNING' || s === 'PENDING';

/** A domain's one-line summary — "3 sources · all healthy · last sync 6m ago". */
function domainSummary(d: DomainHealth): string {
  if (d.counts.total === 0) return 'No sources connected yet';
  const parts: string[] = [`${d.counts.total} source${d.counts.total === 1 ? '' : 's'}`];
  if (d.counts.error > 0) parts.push(`${d.counts.error} failing`);
  else if (d.counts.attention > 0) parts.push(`${d.counts.attention} need attention`);
  else if (d.counts.syncing > 0) parts.push(`${d.counts.syncing} syncing`);
  else parts.push('all healthy');
  parts.push(d.lastSyncedAt ? `last sync ${timeAgo(d.lastSyncedAt)}` : 'never synced');
  return parts.join(' · ');
}

export function ActivityPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role ? roleHasPermissions(role, [Permission.OrgManage]) : false;

  const healthQ = useQuery({
    queryKey: ['domain-health'],
    queryFn: getDomainHealth,
    enabled: canManage,
  });
  const membersQ = useQuery({
    queryKey: ['members'],
    queryFn: listMembers,
    enabled: canManage,
  });

  const domains = healthQ.data ?? [];
  const members = membersQ.data ?? [];

  const summary = useMemo(() => {
    const total = domains.length;
    const healthy = domains.filter((d) => d.overall === 'healthy').length;
    const needAttention = domains.filter(
      (d) => d.overall === 'error' || d.overall === 'attention',
    ).length;
    const weekAgo = Date.now() - 7 * 864e5;
    const activeThisWeek = members.filter(
      (m) => m.lastLoginAt && new Date(m.lastLoginAt).getTime() >= weekAgo,
    ).length;
    return { total, healthy, needAttention, memberCount: members.length, activeThisWeek };
  }, [domains, members]);

  // Most-recently-active first; members who never signed in sink to the bottom.
  const sortedMembers = useMemo(
    () =>
      [...members].sort((a, b) => {
        const ta = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : -1;
        const tb = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : -1;
        return tb - ta;
      }),
    [members],
  );

  // Paginate the roster at 25/page (domains stay unpaginated — capped at 10).
  const membersPager = usePagination(sortedMembers, 25);

  if (!canManage) {
    return (
      <div className="space-y-6">
        <h1 className="font-display text-2xl font-bold">Activity</h1>
        <Card className="p-8 text-center text-sm text-muted">
          This page is available to organization administrators.
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold">Activity</h1>
        <p className="text-sm text-muted">
          Whether every domain is syncing cleanly, and who on your team is active.
        </p>
      </div>

      {/* Summary strip — state before detail. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryStat
          label="Domains healthy"
          value={`${summary.healthy}`}
          suffix={`/ ${summary.total}`}
          tone={summary.total > 0 && summary.healthy === summary.total ? 'ok' : undefined}
        />
        <SummaryStat
          label="Need attention"
          value={`${summary.needAttention}`}
          tone={summary.needAttention > 0 ? 'bad' : undefined}
        />
        <SummaryStat label="Members" value={`${summary.memberCount}`} />
        <SummaryStat
          label="Active this week"
          value={`${summary.activeThisWeek}`}
          suffix={`/ ${summary.memberCount}`}
          tone="ok"
        />
      </div>

      {/* Section 1 — Domain health. */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-haze">Domain health</h2>
          <span className="text-xs text-muted">Sorted by what needs attention first</span>
        </div>

        {healthQ.isLoading && <SkeletonRows rows={3} rightBlocks={0} />}
        {healthQ.isError && (
          <Card className="p-6 text-center text-sm text-muted">Could not load domain health.</Card>
        )}
        {healthQ.data && domains.length === 0 && (
          <Card className="p-8 text-center text-sm text-muted">No domains yet.</Card>
        )}
        {domains.map((d) => (
          <DomainHealthCard key={d.domainId} domain={d} />
        ))}
      </section>

      {/* Section 2 — Team activity. */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-haze">Team activity</h2>
          <span className="text-xs text-muted">Sorted by most recently active</span>
        </div>

        {membersQ.isLoading && <SkeletonRows rows={4} rightBlocks={0} />}
        {membersQ.isError && (
          <Card className="p-6 text-center text-sm text-muted">Could not load team activity.</Card>
        )}
        {membersQ.data && (
          <>
            <MembersTable members={membersPager.pageItems} totalDomains={domains.length} />
            <Pagination
              page={membersPager.page}
              pageCount={membersPager.pageCount}
              total={membersPager.total}
              start={membersPager.start}
              shown={membersPager.shown}
              onPrev={membersPager.prev}
              onNext={membersPager.next}
            />
          </>
        )}
      </section>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  suffix,
  tone,
}: {
  label: string;
  value: string;
  suffix?: string;
  tone?: 'ok' | 'bad';
}) {
  const valueColor = tone === 'ok' ? 'text-iris' : tone === 'bad' ? 'text-danger' : 'text-bone';
  return (
    <Card className="p-4">
      <div className="mb-2 text-xs uppercase tracking-wide text-muted">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${valueColor}`}>
        {value}
        {suffix && <span className="ml-1 text-sm font-medium text-muted">{suffix}</span>}
      </div>
    </Card>
  );
}

function DomainHealthCard({ domain }: { domain: DomainHealth }) {
  const meta = HEALTH_META[domain.overall];
  const broken = domain.integrations.find((i) => isBrokenIntegration(i.status));
  return (
    <Card className={`border-l-2 p-5 ${meta.stripe}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-base font-bold text-bone">{domain.name}</div>
          <div className="mt-0.5 text-xs text-muted">{domainSummary(domain)}</div>
        </div>
        <Badge variant={meta.badge} dot>
          {meta.label}
        </Badge>
      </div>

      {domain.integrations.length > 0 && (
        <div className="mt-4 border-t border-hairline">
          {domain.integrations.map((i) => (
            <IntegrationRow key={i.id} integration={i} />
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-4 text-xs font-semibold">
        {broken && (
          <Link
            to={`/domains/${domain.domainId}/integrations`}
            className="text-iris hover:text-iris-strong"
          >
            Fix {PROVIDER[broken.provider].label} →
          </Link>
        )}
        <Link
          to={`/domains/${domain.domainId}/overview`}
          className="text-iris hover:text-iris-strong"
        >
          Open domain
        </Link>
      </div>
    </Card>
  );
}

function IntegrationRow({ integration: i }: { integration: IntegrationHealthRow }) {
  const prov = PROVIDER[i.provider];
  return (
    <div className="grid grid-cols-[minmax(120px,auto)_1fr_auto] items-center gap-3 border-b border-hairline py-2.5 last:border-b-0">
      <div className="flex items-center gap-2 text-sm font-semibold text-bone">
        <ProviderLogo provider={i.provider} size={16} />
        {prov.label}
      </div>
      <div className="flex min-w-0 items-center gap-2 text-xs text-haze">
        <span className={`h-2 w-2 flex-none rounded-full ${INT_DOT[i.status]}`} aria-hidden />
        <span>{INT_LABEL[i.status]}</span>
        {i.lastError && (
          <span className="truncate text-muted" title={i.lastError}>
            — {i.lastError}
          </span>
        )}
      </div>
      <div className="whitespace-nowrap text-right text-xs text-muted">
        {i.lastSyncedAt ? `synced ${timeAgo(i.lastSyncedAt)}` : 'never synced'}
      </div>
    </div>
  );
}

function MembersTable({ members, totalDomains }: { members: Member[]; totalDomains: number }) {
  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full min-w-[640px] border-collapse">
        <thead>
          <tr className="border-b border-hairline">
            <Th>Member</Th>
            <Th>Role</Th>
            <Th align="right">Domains</Th>
            <Th>2FA</Th>
            <Th>Last login</Th>
            <Th>Joined</Th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id} className="border-b border-hairline last:border-b-0 hover:bg-surface-2">
              <td className="p-4">
                <div className="flex items-center gap-3">
                  <Avatar name={m.name} email={m.email} size={30} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-bone">
                      {m.name ?? m.email}
                    </div>
                    <div className="truncate text-[0.7rem] text-muted">{m.email}</div>
                  </div>
                </div>
              </td>
              <td className="p-4">
                <Badge variant={m.role === 'ADMINISTRATOR' ? 'success' : 'neutral'}>
                  {m.role.toLowerCase()}
                </Badge>
              </td>
              <td className="p-4 text-right text-sm tabular-nums text-haze">
                {m.role === 'ADMINISTRATOR' ? (
                  <span title="Administrators can access every domain">All ({totalDomains})</span>
                ) : (
                  m.domainIds.length
                )}
              </td>
              <td className="p-4 text-sm">
                {m.mfaEnabled ? (
                  <span className="font-semibold text-success">On</span>
                ) : (
                  <span className="text-muted">Off</span>
                )}
              </td>
              <td className="whitespace-nowrap p-4 text-sm">
                {m.lastLoginAt ? (
                  <span className="text-bone">{timeAgo(m.lastLoginAt)}</span>
                ) : (
                  <span className="text-warning">Never signed in</span>
                )}
              </td>
              <td className="whitespace-nowrap p-4 text-xs text-muted">
                {joinedLabel(m.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`p-4 text-xs font-semibold uppercase tracking-wide text-muted ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}
